// Lightweight analytics layer used by NegoIA.
// What it does:
//   1. Generates one stable session ID per browser tab (sessionStorage).
//   2. Calls POST /api/analytics/visit ONCE per session (deduped).
//   3. Exposes trackEvent(name, payload) which:
//        - Sends the event to our backend (so we can export it as CSV).
//        - Forwards the same event to Google Analytics 4 via gtag.
//
// All network calls are best-effort — analytics MUST NOT block user flows or
// surface errors to the UI.

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "negoia:session:id";
const VISIT_FIRED_KEY = "negoia:session:visit-recorded";
const UTM_KEY = "negoia:utm";
const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign"];

// Read utm_source / utm_medium / utm_campaign from the landing URL (if present)
// and persist them to localStorage so we can attribute every subsequent event
// in this session to the campaign that brought the user in. Runs once on load;
// only overwrites stored UTMs when the current URL actually carries them.
function captureUtm() {
  try {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const found = {};
    UTM_FIELDS.forEach((f) => {
      const v = params.get(f);
      if (v) found[f] = v.slice(0, 200);
    });
    if (Object.keys(found).length > 0) {
      localStorage.setItem(UTM_KEY, JSON.stringify(found));
    }
  } catch {
    /* localStorage blocked / no URL — attribution is best-effort */
  }
}

export function getUtm() {
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

// Capture immediately on module load — this is the first chance we get at the
// landing URL before any client-side navigation strips the query string.
captureUtm();

function generateId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for older browsers — entropy is "good enough" for session keying.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = generateId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // SessionStorage can be blocked in private modes — fall back to a per-call ID.
    return generateId();
  }
}

function postJson(path, body) {
  // Use fetch with keepalive so the event survives tab unloads (e.g. when the
  // user immediately navigates to WhatsApp via the <a> click).
  try {
    return fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => null);
  } catch {
    return null;
  }
}

export function trackVisit() {
  // Fire once per browser session, never twice.
  try {
    if (sessionStorage.getItem(VISIT_FIRED_KEY)) return;
    sessionStorage.setItem(VISIT_FIRED_KEY, "1");
  } catch {
    /* private mode — still emit, just possibly twice */
  }
  postJson("/api/analytics/visit", {
    session_id: getSessionId(),
    path: typeof window !== "undefined" ? window.location.pathname : "",
    referrer: typeof document !== "undefined" ? document.referrer : "",
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    ...getUtm(),
  });
}

const ADMIN_MARKED_KEY = "negoia:session:admin-marked";
const TEST_DEVICE_KEY = "negoia:test_device";

// Flag this browser session as an admin session so the analytics summary can
// exclude all of its events from the public event counts. Fired once per
// session from the ?admin=1 route.
export function markAdminSession() {
  try {
    if (sessionStorage.getItem(ADMIN_MARKED_KEY)) return;
    sessionStorage.setItem(ADMIN_MARKED_KEY, "1");
  } catch {
    /* private mode — still emit, just possibly twice (idempotent server-side) */
  }
  postJson("/api/analytics/admin-visit", { session_id: getSessionId() });
}

// "Test device": a persistent (localStorage) flag the owner sets from the admin
// panel. When on, EVERY session on this browser is excluded from the analytics
// event counts — even when browsing the normal app without ?admin=1.
export function isTestDevice() {
  try {
    return localStorage.getItem(TEST_DEVICE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setTestDevice(on) {
  try {
    if (on) localStorage.setItem(TEST_DEVICE_KEY, "1");
    else localStorage.removeItem(TEST_DEVICE_KEY);
  } catch {
    /* ignore */
  }
  if (on) {
    // Exclude the current session right away (re-mark even if already flagged).
    try {
      sessionStorage.removeItem(ADMIN_MARKED_KEY);
    } catch {
      /* ignore */
    }
    markAdminSession();
  }
}

export function trackEvent(name, payload = {}) {
  // Merge campaign attribution into every event so backend CSV + GA4 both
  // carry the utm_* that brought this session in. Explicit payload wins on any
  // key collision.
  const enriched = { ...getUtm(), ...payload };
  // 1. Backend (gives us CSV export + admin dashboard).
  const posted = postJson("/api/analytics/event", {
    session_id: getSessionId(),
    name,
    payload: enriched,
    path: typeof window !== "undefined" ? window.location.pathname : "",
  });
  // 2. Google Analytics 4 (gives the user the GA dashboard they already use).
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, enriched);
    }
  } catch {
    /* gtag may not be ready yet — fine, we already wrote to backend */
  }
  // 3. Hot-campaign signal — the backend tells us (once per campaign per day)
  // when a conversion just pushed a campaign past its daily threshold. Forward
  // that to GA4 as `campana_caliente` so the user's native GA4 alert can fire.
  if (name === "clic_conversion" && posted && typeof posted.then === "function") {
    posted
      .then((res) => (res && res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.campana_caliente) {
          try {
            if (typeof window !== "undefined" && typeof window.gtag === "function") {
              window.gtag("event", "campana_caliente", {
                campaign: data.campana_caliente.campaign,
                conversions: data.campana_caliente.conversions,
                ...getUtm(),
              });
            }
          } catch {
            /* gtag not ready — non-critical */
          }
        }
      })
      .catch(() => {});
  }
}

// On every page load, if this browser is flagged as a test device, exclude the
// current session from the analytics counts — no ?admin=1 required.
if (isTestDevice()) markAdminSession();
